import type { DeviceGroupSummary } from '../../lib/api'

/**
 * Who a publish assigns a policy to, as the publish dialog offers it.
 *
 * `all` and `groups` are what the dialog can draw. `keep` is everything else: a policy assigned to
 * single devices through the API is re-sent exactly as it is, because the assignment endpoint
 * replaces the whole set and a dialog that could only re-send "every device" or a list of groups
 * would silently drop the devices.
 */
export type PublishTargets =
  | { kind: 'all' }
  | { kind: 'groups'; groupIds: string[] }
  | { kind: 'keep'; targets: AssignmentTarget[] }

export interface AssignmentTarget {
  kind: string
  targetId?: string
}

/**
 * What the policy is assigned to now, as a choice. Nothing assigned (never published) and anything
 * containing "all" both read as every device, which is what the dialog offered before it offered
 * anything else.
 */
export function targetsFrom(current: { kind: string; targetId?: string | null }[] | undefined): PublishTargets {
  if (!current || current.length === 0 || current.some((target) => target.kind === 'all')) return { kind: 'all' }
  if (current.every((target) => target.kind === 'group' && target.targetId))
    return { kind: 'groups', groupIds: current.map((target) => target.targetId!) }
  return {
    kind: 'keep',
    targets: current.map((target) => (target.targetId ? { kind: target.kind, targetId: target.targetId } : { kind: target.kind }))
  }
}

export function toAssignment(targets: PublishTargets): AssignmentTarget[] {
  switch (targets.kind) {
    case 'all':
      return [{ kind: 'all' }]
    case 'groups':
      return targets.groupIds.map((targetId) => ({ kind: 'group', targetId }))
    default:
      return targets.targets
  }
}

/** The confirming button's object: "every device", "Finance", "3 device groups". */
export function describeTargets(targets: PublishTargets, groups: DeviceGroupSummary[]): string {
  switch (targets.kind) {
    case 'all':
      return 'every device'
    case 'groups': {
      if (targets.groupIds.length === 1) {
        const only = groups.find((group) => group.deviceGroupId === targets.groupIds[0])
        return only ? only.name : '1 device group'
      }
      return `${targets.groupIds.length} device groups`
    }
    default:
      return 'what it is assigned to now'
  }
}
